import { ALO_OMS_HOST } from 'src/scripts/utils/constants';
import { BopisProductStoreAvailability, BopisProductsStoreAvailability } from './bopisTypes';
import { CartData } from 'src/scripts/utils/cart';
import { IStoreAvailabilityData, StoreLocatorResponseError } from '../StoreLocator/storeLocatorTypes';
import { bopisState, setIsFetchingData } from './bopisState';
import { customerLocationState } from '../StoreLocator/customerLocationState';
import { isEmpty } from 'lodash-es';

export const getBopisDestinationObject = (
  coordinates: { latitude: string; longitude: string } | null,
  zip: string | null
) => {
  if (!coordinates && !zip) return null;
  return coordinates
    ? {
        latitude: parseFloat(coordinates.latitude),
        longitude: parseFloat(coordinates.longitude),
      }
    : zip
    ? {
        postal_code: zip,
      }
    : null;
};

export const checkBopisAvailabilityByProducts = async (
  products: BopisProductStoreAvailability[],
  shouldUseLocationId = false
) => {
  const filteredProducts = products.filter((item) => item.quantity > 0); // [REVISIT] Once OMS team fixed the handling of out of stock products. Or once item with "0" qty is removed from the cart.

  const destination = getBopisDestinationObject(customerLocationState.coordinates, customerLocationState.zip);
  if (
    !(destination?.latitude && destination?.longitude) &&
    !destination?.postal_code &&
    isEmpty(bopisState.storeSelection)
  )
    return { detail: 'Location is unknown' };
  setIsFetchingData(true);

  const requestBody = {
    rate: {
      destination: shouldUseLocationId ? { location_id: bopisState?.storeSelection?.[0]?.location_id } : destination, // Fetch based on user selected store if isCart === true;
      items: filteredProducts,
    },
  };

  const response = await fetch(`${ALO_OMS_HOST}/ias/api/v1/find-availability/pickup?source=product_availability`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    console.error(`HTTP error! status: ${response.status}`);
    setIsFetchingData(false);
    return { detail: response.status.toString() };
  }
  const data: BopisProductsStoreAvailability | StoreLocatorResponseError = await response.json();
  setIsFetchingData(false);
  return data;
};

export const checkBopisAvailabilityByCartForSidebar = async (items: BopisProductStoreAvailability[]) => {
  const destination = getBopisDestinationObject(customerLocationState.coordinates, customerLocationState.zip);
  if (!(destination?.latitude && destination?.longitude) && !destination?.postal_code)
    return { detail: 'Location is unknown' };
  const filteredItems = items.filter((item) => item.quantity > 0); // [REVISIT] Once OMS team fixed the handling of out of stock products. Or once item with "0" qty is removed from the cart.
  setIsFetchingData(true);
  const requestBody = {
    rate: {
      destination,
      items: filteredItems,
    },
  };
  const response = await fetch(`${ALO_OMS_HOST}/ias/api/v1/find-availability/pickup?source=cart_availability`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    console.error(`HTTP error! status: ${response.status}`);
    setIsFetchingData(false);
    return { detail: response.status.toString() };
  }
  const data: IStoreAvailabilityData[] | StoreLocatorResponseError = await response.json();
  setIsFetchingData(false);
  return data;
};

export const mapCartItemsToBopisParamItems = (cartData: CartData) => {
  const { giftItemsAndLoyaltyRewards = [], regularItems = [] } = cartData;
  return [...giftItemsAndLoyaltyRewards, ...regularItems].map((item) => ({
    sku: item.sku,
    quantity: item.quantity,
    requires_shipping: item.requires_shipping,
    isGWP: item.properties?._isGWP || false,
  }));
};

export const extractStoresDataFromBopisProductAvailabilityData = (
  productAvailabilityData: BopisProductsStoreAvailability
) => {
  let stores: IStoreAvailabilityData[] = [];
  if ('availabilityByProducts' in productAvailabilityData) {
    const pickupFulfillmentNode = productAvailabilityData.availabilityByProducts[0]?.availabilityByFulfillmentTypes.find(
      (type) => type.fulfillmentType.toLowerCase() === 'pickup'
    );
    stores = pickupFulfillmentNode?.availabilityDetails[0]?.availabilityByLocations || [];
  }
  return stores;
};

export const checkProductAvailability = (sku: string) => {
  const product = bopisState.cartProductsAvailability?.availabilityByProducts.find(
    (product) => product.productId === sku
  );
  return product;
};

export const getBopisStoreStockAvailabilility = (sku: string): { isAvailable: boolean; quantity: number } => {
  const selectedStoreId = bopisState.storeSelection?.[0]?.location_id;
  if (!bopisState.cartProductsAvailability || !selectedStoreId) return { isAvailable: false, quantity: -1 };

  const product = checkProductAvailability(sku);
  if (!product) return { isAvailable: false, quantity: -1 };

  const allLocations = (product.availabilityByFulfillmentTypes || []).flatMap((fulfillment) =>
    (fulfillment.availabilityDetails || []).flatMap((detail) => detail.availabilityByLocations || [])
  );
  const location = allLocations.find((loc) => loc.location_id === selectedStoreId);
  return location
    ? {
        isAvailable: location.available_after_safety_stock > 0,
        quantity: location.available_after_safety_stock,
      }
    : { isAvailable: false, quantity: -1 };
};
